import {
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { track } from '../runtime/analytics'
import { activeElementWithin } from '../runtime/focus'
import { Portal } from '../runtime/portal'
import { signOutTo, useSession } from '../state/session'

export interface LoginButtonProps {
  loginUrl?: string
  accountUrl?: string
  /**
   * Where logging out lands — the home page by default, so an account page
   * is never left showing content the visitor is no longer signed in for.
   */
  logoutUrl?: string
  /** Signed-out label. */
  label?: string
  /** Signed-in label when the user's name isn't known yet. */
  accountLabel?: string
}

/**
 * A plain text link in the header's own style. The same box signed in and
 * out, so the nav never changes shape and the menu positions from it either way.
 */
const TRIGGER_CLASS =
  'inline-flex h-10 items-center gap-2 rounded-sm font-nav text-sm font-medium text-nav-ink transition outline-none hover:text-brand focus-visible:ring-2 focus-visible:ring-brand-ring'

export function LoginButton({
  loginUrl = '/login',
  accountUrl = '/account',
  logoutUrl = '/',
  label = 'Sign In',
  accountLabel = 'My Account',
}: LoginButtonProps) {
  const status = useSession((state) => state.status)

  // The session hydrates from cookies synchronously at module load, so this is
  // correct on first paint — no flash of the signed-out button on every page.
  if (status !== 'authenticated') {
    return (
      <a href={loginUrl} className={TRIGGER_CLASS} data-testid="login-link">
        {label}
      </a>
    )
  }

  return <AccountMenu accountUrl={accountUrl} accountLabel={accountLabel} logoutUrl={logoutUrl} />
}

function AccountMenu({
  accountUrl,
  accountLabel,
  logoutUrl,
}: {
  accountUrl: string
  accountLabel: string
  logoutUrl: string
}) {
  const user = useSession((state) => state.user)

  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState({ top: 0, right: 0 })
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const name = user?.name?.trim() || accountLabel

  /**
   * The menu is `position: fixed` against coordinates read from the trigger.
   *
   * A portal cannot help here: our portal host lives inside this component's
   * own shadow root, which is itself inside whatever Webflow nav container is
   * clipping us — so portalling escapes nothing. Fixed positioning does escape
   * `overflow: hidden` ancestors while keeping the menu inside the shadow root
   * where our styles still apply.
   */
  const reposition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) return
    setPosition({
      top: rect.bottom + 8,
      right: Math.max(8, window.innerWidth - rect.right),
    })
  }, [])

  useLayoutEffect(() => {
    if (open) reposition()
  }, [open, reposition])

  useEffect(() => {
    if (!open) return

    function onPointerDown(event: Event) {
      // composedPath() is required: an event originating inside a shadow root
      // is retargeted to the host by the time it reaches the document, so the
      // usual `contains(event.target)` check would always report "outside".
      const path = event.composedPath()
      if (triggerRef.current && path.includes(triggerRef.current)) return
      if (menuRef.current && path.includes(menuRef.current)) return
      setOpen(false)
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      setOpen(false)
      triggerRef.current?.focus()
    }

    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('keydown', onKeyDown)
    window.addEventListener('resize', reposition)
    // Capture phase catches scrolling in any ancestor, not just the window.
    window.addEventListener('scroll', reposition, true)

    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [open, reposition])

  // Move focus into the menu when it opens, so keyboard users land somewhere.
  useEffect(() => {
    if (!open) return
    const first = menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')
    first?.focus()
  }, [open])

  function onMenuKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
    event.preventDefault()

    const items = [...(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])]
    if (items.length === 0) return

    const current = activeElementWithin(menuRef.current)
    const index = items.indexOf(current as HTMLElement)
    const delta = event.key === 'ArrowDown' ? 1 : -1
    const next = (index + delta + items.length) % items.length
    items[next]?.focus()
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={TRIGGER_CLASS}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        data-testid="account-trigger"
      >
        <span className="max-w-40 truncate">{name}</span>
        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" fill="none">
          <path
            d="M2.5 4.5 6 8l3.5-3.5"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <Portal>
          <div
            ref={menuRef}
            role="menu"
            aria-label={accountLabel}
            onKeyDown={onMenuKeyDown}
            style={{ position: 'fixed', top: position.top, right: position.right }}
            className="z-50 min-w-48 rounded-field border border-black/5 bg-white p-1 font-nav shadow-xl"
            data-testid="account-menu"
          >
            <a
              href={accountUrl}
              role="menuitem"
              className="block rounded-md px-3 py-2 text-sm text-ink outline-none hover:bg-brand-soft focus-visible:bg-brand-soft"
              data-testid="account-link"
            >
              My Account
            </a>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                track({ kind: 'logout' })
                signOutTo(logoutUrl)
              }}
              className="block w-full rounded-md px-3 py-2 text-left text-sm text-ink outline-none hover:bg-brand-soft focus-visible:bg-brand-soft"
              data-testid="logout"
            >
              Log out
            </button>
          </div>
        </Portal>
      )}
    </>
  )
}
